import { useEffect, useState, useCallback } from "react";
import { fmtDate } from "../lib/date";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Plus, Copy, QrCode, Tag as TagIcon, WarningCircle, PencilSimple, X } from "@phosphor-icons/react";

const REF = {};

function useRefs() {
  const [refs, setRefs] = useState({ platforms: [], mans: [], statuses: [], depts: [], teams: [], people: [] });
  useEffect(() => {
    Promise.all([
      api.get("/havs/platforms"), api.get("/config/manufacturers"), api.get("/config/asset-statuses"),
      api.get("/config/departments"), api.get("/config/teams"), api.get("/people"),
    ]).then(([p, m, s, d, t, pe]) => setRefs({ platforms: p.data, mans: m.data, statuses: s.data, depts: d.data, teams: t.data, people: pe.data })).catch(() => {});
  }, []);
  return refs;
}

const FIELDS = (refs) => [
  ["battery_platform_id", "Battery platform", "select", refs.platforms],
  ["manufacturer_id", "Manufacturer", "select", refs.mans],
  ["model_text", "Model", "text"],
  ["voltage", "Voltage", "text"],
  ["capacity_ah", "Capacity (Ah)", "number"],
  ["serial_number", "Serial number", "text"],
  ["purchase_date", "Purchase / in-service", "date"],
  ["department_id", "Department", "select", refs.depts],
  ["team_id", "Team", "select", refs.teams],
  ["assigned_person_id", "Assigned person", "person", refs.people],
  ["status_id", "Status", "select", refs.statuses],
  ["notes", "Notes", "text"],
];

function EditDrawer({ battery, refs, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...battery }));
  const save = async () => {
    try {
      const body = {};
      FIELDS(refs).forEach(([k]) => { body[k] = form[k] ?? null; });
      body.asset_number = form.asset_number;
      const r = await api.put(`/havs/batteries/${battery.id}`, body);
      toast.success("Saved"); onSaved(r.data);
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="battery-edit-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-black">{battery.asset_number} — Edit</h3>
          <button data-testid="battery-edit-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="flex flex-col gap-2">
          <div><label className="text-xs font-semibold text-slate-600">Asset number</label>
            <Input data-testid="battery-f-asset_number" value={form.asset_number || ""} onChange={(e) => setForm({ ...form, asset_number: e.target.value })} className="h-9" /></div>
          {FIELDS(refs).map(([k, label, type, opts]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-slate-600">{label}</label>
              {type === "select" || type === "person" ? (
                <select data-testid={`battery-f-${k}`} className="h-9 border-2 border-slate-300 rounded px-2 w-full text-sm" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value || null })}>
                  <option value="">—</option>
                  {(opts || []).map((o) => <option key={o.id} value={o.id}>{type === "person" ? `${o.first_name} ${o.last_name}` : o.name}</option>)}
                </select>
              ) : (
                <Input data-testid={`battery-f-${k}`} type={type} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9" />
              )}
            </div>
          ))}
        </div>
        <Button data-testid="battery-edit-save" onClick={save} className="mt-4 w-full h-10 bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>
      </div>
    </div>
  );
}

function TagDrawer({ battery, onClose, onChanged }) {
  const [tags, setTags] = useState([]);
  const [f, setF] = useState({ media: "qr", position: "", description: "" });
  const load = useCallback(() => api.get(`/tags?target_id=${battery.id}`).then((r) => setTags(r.data)).catch(() => {}), [battery.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { try { await api.post("/tags", { target_type: "asset", target_id: battery.id, ...f }); toast.success("Tag issued"); load(); onChanged(); } catch (e) { toast.error("Failed"); } };
  const revoke = async (id) => { try { await api.post(`/tags/${id}/revoke`, {}); load(); onChanged(); } catch (e) { toast.error("Failed"); } };
  const replace = async (id) => { try { await api.post(`/tags/${id}/replace`); load(); onChanged(); toast.success("Replaced"); } catch (e) { toast.error("Failed"); } };
  const showQr = async (id) => {
    try { const r = await api.get(`/tags/${id}/qr`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error("Failed"); }
  };
  const copyUrl = (t) => { navigator.clipboard?.writeText(`${window.location.origin}/t/${t.token}`); toast.success("Link copied"); };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="battery-tag-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-black">{battery.asset_number} — NFC / QR tags</h3>
          <button data-testid="battery-tag-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="space-y-2 mb-4">
          {tags.filter((t) => t.is_active).length === 0 && <div className="text-amber-700 text-sm font-semibold flex items-center gap-1"><WarningCircle weight="fill" /> No active NFC/QR tag</div>}
          {tags.map((t) => (
            <div key={t.id} className={`border rounded p-2 text-sm ${t.is_active ? "border-slate-200" : "border-slate-100 opacity-50"}`} data-testid={`battery-tag-${t.id}`}>
              <div className="flex items-center gap-2">
                <span className="uppercase text-[10px] font-bold bg-slate-100 rounded px-1">{t.media}</span>
                <span className="flex-1 truncate font-mono text-xs">{t.token}</span>
                {t.is_active && <span className="text-emerald-600 text-xs font-bold">active</span>}
              </div>
              {t.position && <div className="text-xs text-slate-500">{t.position}</div>}
              <div className="flex gap-2 mt-1 flex-wrap">
                <button data-testid={`battery-tag-qr-${t.id}`} onClick={() => showQr(t.id)} className="text-blue-700 text-xs font-semibold inline-flex items-center gap-0.5"><QrCode size={13} />QR</button>
                <button onClick={() => copyUrl(t)} className="text-slate-600 text-xs font-semibold inline-flex items-center gap-0.5"><Copy size={13} />Copy link</button>
                {t.is_active && <button data-testid={`battery-tag-replace-${t.id}`} onClick={() => replace(t.id)} className="text-amber-700 text-xs font-semibold">Replace</button>}
                {t.is_active && <button data-testid={`battery-tag-revoke-${t.id}`} onClick={() => revoke(t.id)} className="text-red-600 text-xs font-semibold">Revoke</button>}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-3 flex flex-col gap-2">
          <div className="font-semibold text-sm">Add NFC / QR tag</div>
          <select data-testid="battery-tag-media" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={f.media} onChange={(e) => setF({ ...f, media: e.target.value })}>
            <option value="qr">QR</option><option value="nfc">NFC</option><option value="both">NFC + QR</option>
          </select>
          <Input data-testid="battery-tag-position" placeholder="Location / description (optional)" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} className="h-9" />
          <Button data-testid="battery-tag-add" onClick={add} className="h-9 bg-slate-900 font-semibold"><Plus size={14} className="mr-1" />Issue tag</Button>
        </div>
      </div>
    </div>
  );
}

export default function BatteriesTab() {
  const refs = useRefs();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", platform_id: "", department_id: "", status_id: "", tagged: "" });
  const [edit, setEdit] = useState(null);
  const [tagFor, setTagFor] = useState(null);
  const [sel, setSel] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [aform, setAform] = useState({});
  const [bulk, setBulk] = useState({ battery_platform_id: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/havs/batteries?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const patchRow = (b) => setRows((rs) => rs.map((r) => (r.id === b.id ? b : r)));
  const addBattery = async (dup) => {
    try {
      if (dup) { const r = await api.post(`/havs/batteries/${dup}/duplicate`); toast.success(`Added ${r.data.asset_number}`); load(); return; }
      const r = await api.post("/havs/batteries", aform); toast.success(`Added ${r.data.asset_number}`); setAddOpen(false); setAform({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doBulk = async () => {
    if (sel.length === 0 || !bulk.battery_platform_id) { toast.error("Select batteries and a platform"); return; }
    try { await api.post("/havs/batteries/bulk", { ids: sel, battery_platform_id: bulk.battery_platform_id }); toast.success("Updated"); setSel([]); load(); } catch (e) { toast.error("Failed"); }
  };
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap items-center">
          <Input data-testid="battery-search" placeholder="Search #, make/model, serial…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="h-9 w-56" />
          <select data-testid="battery-filter-platform" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={filters.platform_id} onChange={(e) => setFilters({ ...filters, platform_id: e.target.value })}>
            <option value="">All platforms</option>{refs.platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select data-testid="battery-filter-dept" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={filters.department_id} onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}>
            <option value="">All departments</option>{refs.depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select data-testid="battery-filter-tagged" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={filters.tagged} onChange={(e) => setFilters({ ...filters, tagged: e.target.value })}>
            <option value="">Tagged & untagged</option><option value="true">Tagged</option><option value="false">Untagged</option>
          </select>
        </div>
        <Button data-testid="battery-add" onClick={() => setAddOpen(true)} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold"><Plus size={16} weight="bold" className="mr-1" />Add Battery</Button>
      </div>

      {sel.length > 0 && (
        <div className="bg-slate-100 border border-slate-200 rounded p-2 flex items-center gap-2 flex-wrap text-sm" data-testid="battery-bulk-bar">
          <span className="font-semibold">{sel.length} selected</span>
          <select data-testid="battery-bulk-platform" className="h-8 border-2 border-slate-300 rounded px-2" value={bulk.battery_platform_id} onChange={(e) => setBulk({ battery_platform_id: e.target.value })}>
            <option value="">Set platform…</option>{refs.platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button data-testid="battery-bulk-apply" onClick={doBulk} className="h-8 bg-slate-900 font-semibold text-xs px-3">Apply to selected</Button>
          <button onClick={() => setSel([])} className="text-slate-500 text-xs underline">Clear</button>
        </div>
      )}

      <div className="bg-white border-2 border-slate-200 rounded-lg overflow-x-auto">
        <table className="text-sm w-full">
          <thead><tr className="bg-slate-100 text-left text-xs text-slate-600">
            <th className="px-2 py-2"></th><th className="px-2 py-2">#</th><th className="px-2 py-2">Make / model</th>
            <th className="px-2 py-2">Platform</th><th className="px-2 py-2">V / Ah</th><th className="px-2 py-2">Dept / team</th>
            <th className="px-2 py-2">Assigned</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Tags</th><th className="px-2 py-2"></th>
          </tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-slate-100" data-testid={`battery-row-${b.id}`}>
                <td className="px-2 py-2"><input type="checkbox" data-testid={`battery-sel-${b.id}`} checked={sel.includes(b.id)} onChange={() => toggleSel(b.id)} /></td>
                <td className="px-2 py-2 font-mono font-bold">{b.asset_number}</td>
                <td className="px-2 py-2">{b.manufacturer_name || "—"} {b.model_text || ""}</td>
                <td className="px-2 py-2">{b.platform_name || <span className="text-amber-700">⚠ none</span>}</td>
                <td className="px-2 py-2 text-slate-600">{b.voltage || "—"}{b.capacity_ah ? ` · ${b.capacity_ah}Ah` : ""}</td>
                <td className="px-2 py-2 text-slate-600">{b.department_name || "—"}{b.team_name ? ` / ${b.team_name}` : ""}</td>
                <td className="px-2 py-2 text-slate-600">{b.assigned_person_name || "—"}</td>
                <td className="px-2 py-2">{b.status_name || "—"}</td>
                <td className="px-2 py-2">
                  {b.tags.active > 0
                    ? <span className="text-emerald-700 font-semibold text-xs">{b.tags.media.join("+").toUpperCase()} ✅ {b.tags.active}{b.tags.last_scan ? ` · scanned ${fmtDate(b.tags.last_scan)}` : ""}</span>
                    : <span className="text-amber-700 font-semibold text-xs" data-testid={`battery-untagged-${b.id}`}>⚠ No tag</span>}
                </td>
                <td className="px-2 py-2 whitespace-nowrap">
                  <button data-testid={`battery-tags-btn-${b.id}`} onClick={() => setTagFor(b)} className="text-blue-700 mr-2" title="Tags"><TagIcon size={16} weight="bold" /></button>
                  <button data-testid={`battery-edit-btn-${b.id}`} onClick={() => setEdit(b)} className="text-slate-700 mr-2" title="Edit"><PencilSimple size={16} weight="bold" /></button>
                  <button data-testid={`battery-dup-btn-${b.id}`} onClick={() => addBattery(b.id)} className="text-slate-500 text-xs">Add like this</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400">No batteries match. Use “Add Battery”.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <EditDrawer battery={edit} refs={refs} onClose={() => setEdit(null)} onSaved={(b) => { patchRow(b); setEdit(null); }} />}
      {tagFor && <TagDrawer battery={tagFor} onClose={() => setTagFor(null)} onChanged={load} />}

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="battery-add-drawer" onClick={() => setAddOpen(false)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-black">Add Battery</h3><button onClick={() => setAddOpen(false)}><X size={20} /></button></div>
            <p className="text-xs text-slate-500 mb-2">Asset number auto-generates (B1, B2…) if left blank.</p>
            <div className="flex flex-col gap-2">
              <Input data-testid="battery-add-number" placeholder="Asset number (optional)" value={aform.asset_number || ""} onChange={(e) => setAform({ ...aform, asset_number: e.target.value })} className="h-9" />
              {FIELDS(refs).map(([k, label, type, opts]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-slate-600">{label}</label>
                  {type === "select" || type === "person" ? (
                    <select data-testid={`battery-add-${k}`} className="h-9 border-2 border-slate-300 rounded px-2 w-full text-sm" value={aform[k] || ""} onChange={(e) => setAform({ ...aform, [k]: e.target.value || null })}>
                      <option value="">—</option>{(opts || []).map((o) => <option key={o.id} value={o.id}>{type === "person" ? `${o.first_name} ${o.last_name}` : o.name}</option>)}
                    </select>
                  ) : <Input data-testid={`battery-add-${k}`} type={type} value={aform[k] ?? ""} onChange={(e) => setAform({ ...aform, [k]: e.target.value })} className="h-9" />}
                </div>
              ))}
            </div>
            <Button data-testid="battery-add-save" onClick={() => addBattery(null)} className="mt-4 w-full h-10 bg-blue-600 hover:bg-blue-700 font-semibold">Add battery</Button>
          </div>
        </div>
      )}
    </div>
  );
}
